import { Context, ErrorEvent as SentryErrorEvent, Event as SentryEvent, StackFrame } from '@sentry/react'
import { Criticality, IdentifierFormats, Logging, LoggingUser, Severity } from 'types/logging.ts'
import { HANDLED_ERRORS } from 'constants/errors.ts'
import * as Sentry from '@sentry/capacitor'
import * as SentryReact from '@sentry/react'
import { IS_PRODUCTION, IS_STAGING, SENTRY_DSN } from 'constants/envs.ts'
import { snakeCase } from 'es-toolkit/string'
import { isPlainObject } from 'es-toolkit/predicate'
import { DENY_URLS, DISOWNED_URLS, PERSONAL_DATA_KEYS, TO_IGNORE_ERRORS } from 'constants/logging.ts'

/**
 * Recursively replaces personal data in a value by key, see {@link PERSONAL_DATA_KEYS}. Empty values are kept as they
 * are, so an event still shows whether a field was filled in.
 *
 * @param {unknown} value
 * @param {string} [key] - The key under which the value is stored in its parent object.
 * @returns {unknown}
 */
const redactValue = (value: unknown, key?: string): unknown => {
  const normalizedKey = key?.toLowerCase()

  if (normalizedKey && PERSONAL_DATA_KEYS.has(normalizedKey)) {
    return value === undefined || value === null || value === '' ? value : '[REDACTED]'
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item))
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([childKey, child]) => [childKey, redactValue(child, childKey)]))
  }

  return value
}

/**
 * Redacts personal data from the contexts, extra data and breadcrumb data of an event. The Sentry user is left alone:
 * it is set on purpose through {@link setLogUser}.
 *
 * @template {SentryEvent} T
 * @param {T} event
 * @returns {T}
 */
export const redactEvent = <T extends SentryEvent>(event: T): T => ({
  ...event,
  ...(event.contexts && { contexts: redactValue(event.contexts) }),
  ...(event.extra && { extra: redactValue(event.extra) }),
  ...(event.breadcrumbs && {
    breadcrumbs: event.breadcrumbs.map((breadcrumb) =>
      breadcrumb.data ? { ...breadcrumb, data: redactValue(breadcrumb.data) } : breadcrumb
    )
  })
})

/**
 * Collects every stack frame across all exception values on an event.
 *
 * @param {SentryErrorEvent} event
 * @returns {StackFrame[]}
 */
const getStackFrames = (event: SentryErrorEvent): StackFrame[] =>
  (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? [])

/**
 * Checks whether an event has a stacktrace in which none of the frames belong to a script we ship.
 *
 * Google's iOS browsers (Chrome for iOS, the Google app) inject their own Closure-compiled scripts into every page as
 * WKWebView user scripts. WebKit attributes those frames to the document URL using the injected script's own line
 * numbers, so they arrive as errors like `Error: Ca` at `/market-prices:444:350` even though that document is only 62
 * lines long. None of our code is on the stack and there is nothing for us to fix, so these are dropped.
 *
 * Events without any frames are kept: an absent stacktrace is not evidence that the error came from elsewhere.
 *
 * @param {SentryErrorEvent} event
 * @returns {boolean}
 */
export const hasNoOwnStackFrames = (event: SentryErrorEvent): boolean => {
  const frames = getStackFrames(event)

  return (
    frames.length > 0 && !frames.some(({ filename }) => ['/assets/', '/src/', '/node_modules/'].some((path) => filename?.includes(path)))
  )
}

/**
 * Checks whether any frame in the stacktrace belongs to a browser extension or a tag-manager-injected third-party
 * script.
 *
 * Sentry's own `denyUrls` only tests one filename per event, so such an error that unwinds through an instrumented
 * global (`fetch`, `setTimeout`, `addEventListener`) keeps a `node_modules/@sentry/...` frame and slips past both
 * `denyUrls` and {@link hasNoOwnStackFrames}. A single disowned frame is enough to drop the whole event: our code is
 * never called from an extension bundle or a consent dialog.
 *
 * @param {SentryErrorEvent} event
 * @returns {boolean}
 */
export const hasDisownedStackFrames = (event: SentryErrorEvent): boolean =>
  getStackFrames(event).some(({ filename }) => DISOWNED_URLS.some((pattern) => pattern.test(filename ?? '')))

/**
 * Checks whether the event is an error whose entire message is a minified identifier and that carries no stacktrace.
 *
 * Google's iOS browsers inject Closure-compiled scripts into every page. When one of those rejects, WebKit gives us the
 * rejection with no frames at all and a message that is just the compiled class name (`La`, `Ea`). Our own code never
 * throws an error whose whole message is a one- or two-character identifier, so there is nothing to lose by dropping it.
 *
 * @param {SentryErrorEvent} event
 * @returns {boolean}
 */
export const isMinifiedThirdPartyError = (event: SentryErrorEvent): boolean => {
  const values = event.exception?.values ?? []
  if (values.length !== 1) return false

  const [{ value, stacktrace }] = values

  return !stacktrace?.frames?.length && !!value && /^[A-Za-z$_][A-Za-z0-9$_]?$/.test(value)
}

/**
 * Checks whether the event originated in code Safari masked as `webkit-masked-url://hidden/`.
 *
 * Safari hides the real filenames of extension and user-script code behind that scheme. Sentry cannot attribute such a
 * rejection to a file, so it arrives as a non-Error `Object captured as promise rejection` with the original stack only
 * present in the serialized payload. None of it is ours.
 *
 * @param {SentryErrorEvent} event
 * @returns {boolean}
 */
export const isMaskedExtensionError = (event: SentryErrorEvent): boolean => {
  const serialized = event.extra?.__serialized__
  const stack = typeof serialized === 'object' && serialized !== null ? (serialized as { stack?: unknown }).stack : undefined

  return typeof stack === 'string' && stack.includes('webkit-masked-url://')
}

/**
 * Redux store enhancer that attaches the current Redux state to Sentry events.
 * Apply it via the store's `enhancers` option so logging stays centralized in this file.
 */
// TODO: Add masks
export const loggingReduxEnhancer = SentryReact.createReduxEnhancer()

export const initializeSentry = () => {
  Sentry.init(
    {
      dsn: SENTRY_DSN,
      environment: IS_PRODUCTION ? 'production' : IS_STAGING ? 'staging' : 'local',
      enableLogs: IS_PRODUCTION || IS_STAGING,
      tracesSampleRate: IS_STAGING ? 1 : 0.1,
      sendDefaultPii: true,
      normalizeDepth: 10,
      ignoreErrors: TO_IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      release: 'my-bolt@1.1.0',
      dist: '1',
      beforeSend(event, hint) {
        if (hint.originalException instanceof Error && hint.originalException.name === 'AbortError') return null
        if (hasNoOwnStackFrames(event)) return null
        if (hasDisownedStackFrames(event)) return null
        if (isMinifiedThirdPartyError(event)) return null
        if (isMaskedExtensionError(event)) return null
        return redactEvent(event)
      },
      beforeSendTransaction: (event) => redactEvent(event)
    },
    // The Capacitor SDK calls this sibling-init with the fully merged options object (a single arg).
    // It must receive that object untouched, so the React SDK initializes with the same DSN/transport/integrations.
    SentryReact.init
  )
}

/**
 * Associates a user with subsequent client Sentry events for the current browser session.
 *
 * @param {LoggingUser} user - The user to associate with Sentry events.
 * @returns {void}
 * @example
 * setLogUser({ id: '123', email: 'user@example.com', name: 'Jane' })
 */
export const setLogUser = (user: LoggingUser): void => {
  // Skip setting user in server environments to prevent cross-request user attribution leaks
  if (typeof window === 'undefined') return

  Sentry.setUser({ id: user.id ?? user.email, email: user.email, username: user.name })
}

/**
 * Attaches arbitrary key-value data to a named context section in Sentry.
 * Useful for capturing structured state (e.g. form values, API responses) alongside errors.
 *
 * @param {string} section - The name of the context section in Sentry.
 * @param {Record<string, unknown>} data - The key-value data to attach to the section.
 * @returns {void}
 * @example
 * setLogContext('formData', { email: 'user@example.com', ean: '123456789' })
 */
export const setLogContext = (section: string, data: Record<string, unknown>): void => {
  Sentry.setContext(snakeCase(section), data)
}

/**
 * Appends a breadcrumb to Sentry's trail, providing an audit log of events leading up to an error.
 *
 * @param {string} message - A short description of the event.
 * @param {Record<string, unknown>} [metadata] - Optional additional data to attach to the breadcrumb.
 * @returns {void}
 * @example
 * addLogBreadcrumb('User submitted registration form', { step: 'address' })
 */
export const addLogBreadcrumb = (message: string, metadata?: Record<string, unknown>): void => {
  Sentry.addBreadcrumb({ message, data: metadata })
}

/**
 * Truncates a string to a specified maximum length for logging purposes.
 * If the string exceeds the maximum length, it is truncated and wrapped in a `TRUNCATED(...)` message.
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export const truncateStringForLogging = (text: string, maxLength: number = 20): string => {
  if (text.length > maxLength) {
    return `TRUNCATED(${text.slice(0, maxLength)})`
  }

  return text
}

/**
 * Extracts the flow name from a structured identifier string of the form `[FLOW_NAME:...]` or `[FLOW_NAME]`.
 * Returns `'unknown'` when the identifier doesn't match the expected format.
 *
 * @param {string} identifier - A structured log identifier, e.g. `[simulation:createProspect]`.
 * @returns {string} The lowercase flow name, or `'unknown'` if parsing fails.
 * @example
 * parseFlowFromIdentifier('[simulation:createProspect]') // 'simulation'
 * parseFlowFromIdentifier('[CMS:career:getStaticProps]') // 'cms'
 * parseFlowFromIdentifier('invalid')                    // 'unknown'
 */
export const parseFlowFromIdentifier = (identifier: string): string => {
  const match = identifier.match(/^\[([A-Za-z0-9_]+)(?::|])/)
  return match?.[1]?.toLowerCase() ?? 'unknown'
}

/**
 * Tracks a count metric in Sentry. This is useful for tracking the number of times a certain event occurs, such as the
 * number of times a specific error is thrown.
 *
 * @param {string} metric
 * @param {Record<string, string | number | boolean>} attributes
 */
export const trackSentryCount = (metric: string, attributes?: Record<string, string | number | boolean>): void => {
  Sentry.metrics.count(metric, 1, { attributes })
}

/**
 * Flattens nested logging metaData into the flat primitive attributes Sentry metrics accept.
 * Nested objects are flattened to `section.key`; non-primitive leaves are dropped.
 *
 * @param {Record<string, Context>} [metaData]
 * @returns {Record<string, string | number | boolean>}
 */
const flattenMetricAttributes = (metaData?: Record<string, Context>): Record<string, string | number | boolean> => {
  if (!metaData) return {}

  return Object.entries(metaData).reduce<Record<string, string | number | boolean>>((acc, [section, data]) => {
    if (!data || typeof data !== 'object') return acc

    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        acc[`${section}.${key}`] = value
      }
    })

    return acc
  }, {})
}

/**
 * Captures an exception in Sentry, tagged with the flow, criticality, and identifier derived from the logging payload.
 *
 * @template {IdentifierFormats} T
 * @param {Logging<T>} params - The logging payload.
 * @param {Error | string} params.error - The error to capture. Strings are wrapped in an `Error`.
 * @param {T} params.identifier - A structured identifier string that encodes the flow and location, e.g. `[simulation:createProspect]`.
 * @param {Severity} [params.severity=Severity.ERROR] - The Sentry severity level.
 * @param {Criticality} [params.criticality=Criticality.MEDIUM] - Business criticality tag used for alerting triage.
 * @param {{ [section: string]: { [key: string]: unknown } }} [params.metaData] - Optional structured context data, keyed by section name.
 * @param {string} [params.errorCode] - Machine-readable response code. Matched against `HANDLED_ERRORS` and set as an `errorCode` tag.
 * @param {string[]} [params.fingerprint] - Explicit Sentry grouping key. Overrides the default grouping described below.
 * @example
 * log({
 *   identifier: '[simulation:createProspect]',
 *   error: new Error('Failed to create prospect'),
 *   severity: Severity.ERROR,
 *   criticality: Criticality.HIGH,
 *   metaData: { simulationRequestData: { ean: '123' } },
 * })
 */
export const log = <T extends IdentifierFormats>({
  error,
  metaData,
  identifier,
  severity = Severity.ERROR,
  criticality = Criticality.MEDIUM,
  errorCode,
  fingerprint
}: Logging<T>) => {
  const isStringError = typeof error === 'string'
  const err = isStringError ? new Error(error) : error
  const flow = parseFlowFromIdentifier(identifier)
  const message = isStringError ? error : error?.message
  const isHandledError = HANDLED_ERRORS.includes(message) || (!!errorCode && HANDLED_ERRORS.includes(errorCode))

  // A string error has no stack of its own: the Error is created right here, so Sentry would group on the message and any
  // interpolated id (ean, contract number, url) would split one problem into an issue per value. Group on what identifies
  // the failure instead. Errors keep Sentry's default stack-based grouping, which separates distinct throws sharing an
  // identifier (e.g. a CATCH block) better than the identifier can.
  const groupingFingerprint = fingerprint ?? (isStringError ? [identifier, errorCode ?? 'no-code'] : undefined)

  if (isHandledError) {
    trackSentryCount(identifier, {
      message,
      ...(errorCode ? { errorCode } : {}),
      ...flattenMetricAttributes(redactValue(metaData) as typeof metaData)
    })
  } else {
    Sentry.withScope((scope) => {
      scope.setTag('flow', flow)
      scope.setTag('criticality', criticality)
      scope.setTag('identifier', identifier)
      scope.setLevel(severity)
      if (errorCode) scope.setTag('errorCode', errorCode)
      if (groupingFingerprint?.length) scope.setFingerprint(groupingFingerprint)
      if (metaData) {
        Object.entries(metaData).forEach(([section, data]) => scope.setContext(section, data))
      }
      Sentry.captureException(err)
    })
  }
}
